import tw from 'twin.macro'
import React, { Fragment, useRef, useState } from 'react'
import { Combobox } from '@headlessui/react'
import * as Portal from '@radix-ui/react-portal'
import { CgChevronDown } from 'react-icons/cg'

import { useSetExclusiveTabMutation, useSetTabContentMutation } from 'modules/Layout/layoutApi'
import { useUpdateOneTimeProductMutation } from 'modules/ProductInfo/productInfoApi'

import { createUID } from 'common/utils/createUID'
import { useAlert } from 'common/components/Alert/hooks/useAlert'
import { usePopper } from 'common/hooks/usePopper'
import { inputBase } from 'common/components/Input/Input'
import { SuggestionContainer, SuggestionItem } from 'common/components/Suggestions/Suggestions'
import { Dialog, DialogContent } from 'common/components/Dialog/Dialog'
import { DeleteDialogBanner } from 'common/components/DeleteDialogBanner/DeleteDialogBanner'
import { Tooltip } from 'common/components/Tooltip/Tooltip'

export const contentTypeOptions = [
  { title: 'Rows (recommended)', value: 'tab', description: 'Create rows of content' },
  { title: 'Collection', value: 'collection', description: 'A flexible list of content' },
  { title: 'Program', value: 'program', description: 'A training plan for users to follow, typically 4-12 weeks' },
  { title: 'Workout', value: 'workout', description: 'One traditional workout of a program' },
  { title: 'Video', value: 'video', description: 'One long-form video' },
]

export default function ContentFormatInput({ inputRef, item, coachOrgId, productId, disabled = false }) {
  const { createAlert } = useAlert()
  const comboboxBtnRef = useRef()
  const [setExclusiveTab] = useSetExclusiveTabMutation()
  const [setTabContent] = useSetTabContentMutation()
  const [updateOneTimeProduct] = useUpdateOneTimeProductMutation()

  const [typeUpdateConfirmation, setTypeUpdateConfirmation] = useState(null)

  const itemType = item?.type

  const updateType = (type) => {
    if (type === 'tab') {
      const tabId = createUID()
      const item = { id: tabId, type }
      setExclusiveTab({ coachOrgId, tabId, tab: { visibilityLevel: 'anyone' } })
      updateOneTimeProduct({ coachOrgId, product: { item }, productId })
    } else {
      const item = { type }
      updateOneTimeProduct({ coachOrgId, product: { item }, productId })
    }
  }

  const handleSelect = (newValue) => {
    if (newValue.value !== itemType) {
      if (item?.id) {
        setTypeUpdateConfirmation(newValue.value)
        return
      }
      updateType(newValue.value)
    }
  }

  const handleConfirmChange = () => {
    if (itemType === 'tab' && item.id) {
      setExclusiveTab({ coachOrgId, tabId: item.id, tab: null })
      setTabContent({ coachOrgId, tabId: item.id, tabContent: null })
    }
    updateType(typeUpdateConfirmation)
    createAlert({ text: 'Content format updated!', type: 'success' })
  }

  const handleInputKeyDown = (e) => {
    const acceptedCodes = ['Enter', 'Tab', 'ArrowUp', 'ArrowDown']
    if (!acceptedCodes.includes(e.code)) {
      e.preventDefault()
    } else if (e.code === 'Enter' && !e.shiftKey) {
      comboboxBtnRef.current.click()
    }
  }

  let [popperTrigger, popperContainer] = usePopper({
    placement: 'bottom-start',
    strategy: 'fixed',
  })

  const inputWidth = inputRef?.current?.getBoundingClientRect()?.width

  return (
    <>
      <Combobox value={itemType} onChange={handleSelect}>
        <div ref={popperTrigger} className='w-full'>
          <Combobox.Button ref={comboboxBtnRef} as='div' className='relative w-full' disabled={disabled}>
            <Tooltip
              content={
                <div className='flex flex-col'>
                  <span className='mb-1'>Content format has been locked because the checkout page is published.</span>
                  <span>You can duplicate this product and edit the copy.</span>
                </div>
              }
              open={disabled ? undefined : false}
            >
              <Combobox.Input
                ref={(e) => {
                  inputRef.current = e
                }}
                placeholder='Select content format'
                autoComplete='off'
                displayValue={(value) =>
                  contentTypeOptions.find((opt) => opt.value === value)?.title || 'Select content format'
                }
                onKeyDown={handleInputKeyDown}
                className='text-ellipsis bg-inherit cursor-pointer'
                css={[inputBase, disabled && tw`bg-gray-50 cursor-not-allowed`]}
                disabled={disabled}
              />
              {!disabled && (
                <CgChevronDown className='absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500 w-4 h-4' />
              )}
            </Tooltip>
          </Combobox.Button>
        </div>
        <Portal.Root>
          <Combobox.Options className='z-[999]' ref={popperContainer} modal={false}>
            <SuggestionContainer containerStyle={{ width: inputWidth }}>
              {contentTypeOptions.map((opt) => (
                <Combobox.Option as={Fragment} key={opt.value} value={opt}>
                  {({ selected, active }) => (
                    <SuggestionItem active={active}>
                      <div className='flex items-center justify-between'>
                        <div>
                          <div className='font-semibold'>{opt.title}</div>
                          <div className='text-sm'>{opt.description}</div>
                        </div>
                      </div>
                    </SuggestionItem>
                  )}
                </Combobox.Option>
              ))}
            </SuggestionContainer>
          </Combobox.Options>
        </Portal.Root>
      </Combobox>
      <Dialog open={Boolean(typeUpdateConfirmation)} setOpen={setTypeUpdateConfirmation}>
        <DialogContent>
          <DeleteDialogBanner
            text={
              itemType === 'tab'
                ? `This action will delete all rows you’ve created`
                : 'This will delete the content you added'
            }
            deleteText='Confirm'
            handleDelete={() => handleConfirmChange()}
            typeToConfirm={true}
            createBtnText='Add'
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
